import React from "react";
import { Tabs, Table, Input, Select, Button, Tooltip, Empty } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import {
  COLUMN_LEVEL_HEADERS,
  ALLOWABLE_HEADERS,
  DATA_TYPES,
} from "../../helper/specWorkbook";

const YN_OPTIONS = [
  { value: "", label: "—" },
  { value: "Y", label: "Y" },
  { value: "N", label: "N" },
];
const CASE_OPTIONS = [
  { value: "", label: "—" },
  { value: "Upper", label: "Upper" },
  { value: "Lower", label: "Lower" },
];
const YN_COLS = new Set([
  "Mandatory Column",
  "Not Null Check",
  "Allowable Values",
  "Row Key Field",
]);

// Controlled editor for the parsed spec model. Calls onChange with the full
// model on every edit so the parent always holds the latest.
function SpecEditor({ value, onChange }) {
  const model = value || {};

  const patch = (next) => onChange({ ...model, ...next });

  // ── File Level ──────────────────────────────────────────────
  const updateFileLevel = (key, val) =>
    patch({
      fileLevel: (model.fileLevel || []).map((r) =>
        r._key === key ? { ...r, value: val } : r,
      ),
    });

  const fileLevelTab = (model.fileLevel || []).length ? (
    <div style={{ border: "1px solid #EEF0F3", borderRadius: 8, overflow: "hidden" }}>
      {(model.fileLevel || []).map((r, i) => (
        <div
          key={r._key}
          style={{
            display: "grid",
            gridTemplateColumns: "260px 1fr 120px",
            gap: 12,
            alignItems: "center",
            padding: "8px 14px",
            borderBottom:
              i < model.fileLevel.length - 1 ? "1px solid #EEF0F3" : "none",
            background: i % 2 === 0 ? "#fff" : "#FAFBFC",
          }}
        >
          <span style={{ fontSize: 13, color: "#6B7280", fontWeight: 500 }}>
            {r.setting}
            {/mandatory/i.test(r.requirement || "") && (
              <span style={{ color: "#A32D2D" }}> *</span>
            )}
          </span>
          <Input
            size="small"
            value={r.value}
            onChange={(e) => updateFileLevel(r._key, e.target.value)}
          />
          <span style={{ fontSize: 12, color: "#9CA3AF" }}>{r.requirement}</span>
        </div>
      ))}
    </div>
  ) : (
    <Empty description="No File Level settings" />
  );

  // ── Column Level ────────────────────────────────────────────
  const colRows = model.columnLevel?.rows || [];

  const setColRows = (rows) =>
    patch({ columnLevel: { columns: COLUMN_LEVEL_HEADERS, rows } });

  const updateColCell = (key, field, val) =>
    setColRows(
      colRows.map((r) => (r._key === key ? { ...r, [field]: val } : r)),
    );

  const addColRow = () => {
    const blank = { _key: Date.now() + Math.random() };
    COLUMN_LEVEL_HEADERS.forEach((h) => (blank[h] = ""));
    setColRows([...colRows, blank]);
  };

  const removeColRow = (key) => setColRows(colRows.filter((r) => r._key !== key));

  const moveColRow = (key, dir) => {
    const idx = colRows.findIndex((r) => r._key === key);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= colRows.length) return;
    const next = [...colRows];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setColRows(next);
  };

  const cellInput = (h) => (text, record) => {
    if (h === "Field #")
      return (
        <span style={{ color: "#9CA3AF", fontSize: 12 }}>
          {colRows.findIndex((r) => r._key === record._key) + 1}
        </span>
      );
    if (h === "DataType")
      return (
        <Select
          size="small"
          style={{ width: 110 }}
          value={record[h] || undefined}
          placeholder="type"
          options={DATA_TYPES.map((d) => ({ value: d, label: d }))}
          onChange={(v) => updateColCell(record._key, h, v)}
        />
      );
    if (h === "Case")
      return (
        <Select
          size="small"
          style={{ width: 90 }}
          value={record[h] || ""}
          options={CASE_OPTIONS}
          onChange={(v) => updateColCell(record._key, h, v)}
        />
      );
    if (YN_COLS.has(h))
      return (
        <Select
          size="small"
          style={{ width: 70 }}
          value={record[h] || ""}
          options={YN_OPTIONS}
          onChange={(v) => updateColCell(record._key, h, v)}
        />
      );
    return (
      <Input
        size="small"
        value={record[h]}
        style={{ minWidth: h === "Column Name" ? 150 : 120 }}
        onChange={(e) => updateColCell(record._key, h, e.target.value)}
      />
    );
  };

  const colColumns = [
    ...COLUMN_LEVEL_HEADERS.map((h) => ({
      title: h,
      dataIndex: h,
      key: h,
      width: h === "Field #" ? 56 : undefined,
      render: cellInput(h),
    })),
    {
      title: "",
      key: "ops",
      fixed: "right",
      width: 110,
      render: (_, record) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Tooltip title="Move up">
            <Button
              type="text"
              size="small"
              icon={<ArrowUpOutlined />}
              onClick={() => moveColRow(record._key, -1)}
            />
          </Tooltip>
          <Tooltip title="Move down">
            <Button
              type="text"
              size="small"
              icon={<ArrowDownOutlined />}
              onClick={() => moveColRow(record._key, 1)}
            />
          </Tooltip>
          <Tooltip title="Remove">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeColRow(record._key)}
            />
          </Tooltip>
        </span>
      ),
    },
  ];

  const columnLevelTab = (
    <div>
      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        onClick={addColRow}
        style={{ marginBottom: 10 }}
      >
        Add column
      </Button>
      <Table
        size="small"
        bordered
        rowKey="_key"
        columns={colColumns}
        dataSource={colRows}
        pagination={false}
        scroll={{ x: "max-content", y: 360 }}
      />
    </div>
  );

  // ── Allowable Values ────────────────────────────────────────
  const avRows = model.allowableValues?.rows || [];
  const setAvRows = (rows) =>
    patch({ allowableValues: { columns: ALLOWABLE_HEADERS, rows } });
  const updateAvCell = (key, field, val) =>
    setAvRows(avRows.map((r) => (r._key === key ? { ...r, [field]: val } : r)));
  const addAvRow = () => {
    const blank = { _key: Date.now() + Math.random() };
    ALLOWABLE_HEADERS.forEach((h) => (blank[h] = ""));
    setAvRows([...avRows, blank]);
  };
  const removeAvRow = (key) => setAvRows(avRows.filter((r) => r._key !== key));

  const avColumns = [
    ...ALLOWABLE_HEADERS.map((h) => ({
      title: h,
      dataIndex: h,
      key: h,
      render: (_, record) => (
        <Input
          size="small"
          value={record[h]}
          onChange={(e) => updateAvCell(record._key, h, e.target.value)}
        />
      ),
    })),
    {
      title: "",
      key: "ops",
      width: 50,
      render: (_, record) => (
        <Button
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeAvRow(record._key)}
        />
      ),
    },
  ];

  const allowableTab = (
    <div>
      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined />}
        onClick={addAvRow}
        style={{ marginBottom: 10 }}
      >
        Add allowable value
      </Button>
      <Table
        size="small"
        bordered
        rowKey="_key"
        columns={avColumns}
        dataSource={avRows}
        pagination={false}
        scroll={{ y: 320 }}
      />
    </div>
  );

  return (
    <Tabs
      type="card"
      size="middle"
      style={{ minHeight: 420 }}
      items={[
        { key: "file", label: "File Level", children: fileLevelTab },
        {
          key: "column",
          label: `Column Level${colRows.length ? ` (${colRows.length})` : ""}`,
          children: columnLevelTab,
        },
        {
          key: "allowable",
          label: `Allowable Values${avRows.length ? ` (${avRows.length})` : ""}`,
          children: allowableTab,
        },
      ]}
    />
  );
}

export default SpecEditor;
